/// <amd-module name="CodeCoverage/AllTab/AllTabDataProviderService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

import { CoverageSummary, DirectoryCoverageSummary, DirectoryCoverageSummaryRequest } from "CodeCoverage/Clients/Coverage/Contracts/Coverage";
import { getCoverageClient } from "CodeCoverage/Clients/Coverage/RestClient/Coverage";
import { PathConstants } from "CodeCoverage/ViewCommon/Constants";
import { CodeCoverageAllTabDataProviderContract } from "CodeCoverage/ViewCommon/Contracts/DataProvider";

export interface IAllTabDataProviderService extends IVssService {
    getDirectoryCoverageSummaryDataAsync(project: string, pipelineRunId: number, path?: string): Promise<CoverageSummary[] | undefined>;

    getFirstLevelDirectoryCoverageSummaryData(): CoverageSummary[] | undefined;

    getFirstLevelDirectoryCoverageSummaryDataAsync(pipelineRunId: number): Promise<CoverageSummary[] | undefined>;
}

// exported for testing
export class AllTabDataProviderService extends VssService implements IAllTabDataProviderService {
    private static AllTabDataProviderId = "ms.vss-codecoverage-ci-web.code-coverage-ci-alltab-data-provider";

    public getDirectoryCoverageSummaryDataAsync(project: string, pipelineRunId: number, path?: string): Promise<CoverageSummary[] | undefined> {
        return getCoverageClient(this.pageContext)
            .getDirectoryCoverageSummary({ path: path || "" } as DirectoryCoverageSummaryRequest, project, pipelineRunId)
            .then((directoryCoverageSummary: DirectoryCoverageSummary | undefined) => {
                return this.normalizeData(directoryCoverageSummary);
            });
    }

    public getFirstLevelDirectoryCoverageSummaryData(): CoverageSummary[] | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const codeCoverageAllTabDataProviderData = contributionService.getData<CodeCoverageAllTabDataProviderContract>(
            AllTabDataProviderService.AllTabDataProviderId
        );
        if (!codeCoverageAllTabDataProviderData || !codeCoverageAllTabDataProviderData.directoryCoverageSummary) {
            return undefined;
        } else {
            return this.normalizeData(codeCoverageAllTabDataProviderData.directoryCoverageSummary);
        }
    }

    public getFirstLevelDirectoryCoverageSummaryDataAsync(pipelineRunId: number): Promise<CoverageSummary[] | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService
            .getDataAsync<CodeCoverageAllTabDataProviderContract>(AllTabDataProviderService.AllTabDataProviderId, {
                buildId: pipelineRunId
            })
            .then((codeCoverageAllTabDataProviderData: CodeCoverageAllTabDataProviderContract) => {
                if (!codeCoverageAllTabDataProviderData || !codeCoverageAllTabDataProviderData.directoryCoverageSummary) {
                    return undefined;
                } else {
                    return this.normalizeData(codeCoverageAllTabDataProviderData.directoryCoverageSummary);
                }
            });
    }

    private normalizeData(directoryCoverageSummary: DirectoryCoverageSummary | undefined): CoverageSummary[] | undefined {
        if (directoryCoverageSummary && directoryCoverageSummary.children) {
            directoryCoverageSummary.children.forEach((summary: CoverageSummary) => {
                summary.covered = summary.covered || 0;
                summary.notCovered = summary.notCovered || 0;
                summary.partiallyCovered = summary.partiallyCovered || 0;

                //TODO: Will remove this once backend starts providing same separator for folder and files
                if (summary.path.indexOf(PathConstants.PathSeparator) > 0) {
                    summary.path = summary.path.replace(/\//g, "\\");
                }
            });

            return directoryCoverageSummary.children;
        }

        return undefined;
    }
}

Services.add("IAllTabDataProviderService", { serviceFactory: AllTabDataProviderService });
